import { apiGateway } from "@/_old/services/api-gateway";
import { productTypeIds } from "@/core/utils";
import { useQuery, UseQueryOptions } from "@tanstack/react-query";
import {
  ChartProductUsageData,
  FinancialBlockResponseAdapter,
  GetFinancialBlockParams,
} from "../types";
import { productUsageKeys } from "../utils/query-keys";

const isValidDateLike = (v: string | Date | undefined | null) => {
  if (!v) return false;
  if (typeof v === "string") return /^\d{4}-\d{2}-\d{2}$/.test(v);
  return Number.isFinite(new Date(v).getTime());
};

function adaptToChartData(api: any): ChartProductUsageData {
  const products = Array.isArray(api?.products) ? api.products : [];
  const total = api?.total ?? {};

  return {
    products: products.map((p: any) => ({
      type: Number(p?.type ?? 0),
      monetary_value: String(p?.monetary_value ?? "0"),
      credits_value: Number(p?.credits_value ?? 0),
      courtesy_value: String(p?.courtesy_value ?? "0"),
    })),
    total: {
      monetary_value: String(total?.monetary_value ?? "0"),
      credits_value: Number(total?.credits_value ?? 0),
    },
    courtesy: api?.courtesy ?? null,
    minimum_consumption: api?.minimum_consumption ?? null,
  };
}

export async function fetchProductUsage(
  params: GetFinancialBlockParams,
): Promise<ChartProductUsageData> {
  const searchParams = new URLSearchParams();
  searchParams.set("start_date", `${params.startDate}`);
  searchParams.set("end_date", `${params.endDate}`);

  if (params.city && params.city.length > 0) {
    params.city.forEach((city) => searchParams.append("cities[]", city.name));
  }
  if (params.group_id) searchParams.set("group_id", String(params.group_id));
  if (params.type && params.type.length > 0) {
    params.type.forEach((t) =>
      searchParams.append("types[]", String(productTypeIds[t])),
    );
  }
  if (params.status && params.status.length > 0) {
    params.status.forEach((s) => searchParams.append("status[]", String(s)));
  }
  if (params.users && params.users.length > 0) {
    const userIds = params.users.map((user) => user.user?.id).filter(Boolean);
    userIds.forEach((id) => searchParams.append("users[]", String(id)));
  }
  if (params.spaces && params.spaces.length > 0) {
    params.spaces.forEach((space) => searchParams.append("spaces[]", space.id));
  }

  const { data } = await apiGateway.get<FinancialBlockResponseAdapter<any>>(
    `/api/v2/companies/credits/usage?${searchParams.toString()}`,
  );

  return adaptToChartData(data?.data);
}

export function useProductUsage(
  params: GetFinancialBlockParams,
  options?: UseQueryOptions<
    ChartProductUsageData,
    Error,
    ChartProductUsageData
  >,
) {
  const enabled =
    isValidDateLike(params.startDate) && isValidDateLike(params.endDate);

  return useQuery<ChartProductUsageData, Error>({
    queryKey: productUsageKeys.list(params),
    queryFn: () => fetchProductUsage(params),
    enabled,
    staleTime: 60_000,
    gcTime: 5 * 60_000,
    refetchOnWindowFocus: false,
    ...options,
  });
}
